<template>
  <Modal
    :show="true"
    title=" "
    :dialogMaxWidth="isOwner ? '440px' : '400px'"
    :closeOnBackdrop="false"
    :zIndex="10070"
    :blur-backdrop="false"
    :backdropOpacity="0.35"
    @close="closeModal"
  >
    <template #header>
      <div class="delete-header">
        <div class="delete-header__icon-wrap">
          <span class="delete-header__ring delete-header__ring--outer"></span>
          <span class="delete-header__ring delete-header__ring--inner"></span>
          <div class="delete-header__icon">
            <Icon icon="mdi:trash-can-outline" width="22" height="22" />
          </div>
        </div>
        <div class="delete-header__text">
          <span class="delete-header__eyebrow">
            <span class="delete-header__dot"></span>
            {{ isOwner ? "Irreversible action" : "Confirm deletion" }}
          </span>
          <template v-if="isOwner">
            <h5 class="delete-header__title">Delete organization account</h5>
          </template>
          <template v-else-if="entity === 'account'">
            <h5 class="delete-header__title">Delete account</h5>
          </template>
          <template v-else>
            <h5 class="delete-header__title">
              Delete {{ plural ? "these" : "this" }} {{ entity ?? "record" }}?
            </h5>
          </template>
        </div>
      </div>
    </template>

    <template #body>
      <div class="delete-body">
        <template v-if="isOwner">
          <p class="delete-body__lede">
            You're about to permanently remove your organization. Please review what this means:
          </p>
          <ul class="delete-body__list">
            <li>
              <Icon icon="mdi:close-circle-outline" class="delete-body__list-icon" />
              <span>Your active subscription will be cancelled immediately</span>
            </li>
            <li>
              <Icon icon="mdi:close-circle-outline" class="delete-body__list-icon" />
              <span>All contacts, companies, leads, and meetings will be permanently deleted</span>
            </li>
            <li>
              <Icon icon="mdi:close-circle-outline" class="delete-body__list-icon" />
              <span>All team members will lose access to the organization</span>
            </li>
            <li>
              <Icon icon="mdi:close-circle-outline" class="delete-body__list-icon" />
              <span>All phone numbers will be released</span>
            </li>
          </ul>
          <div class="delete-body__callout">
            <Icon icon="mdi:alert-octagon-outline" width="16" height="16" />
            <span>This action cannot be undone.</span>
          </div>
          <div class="delete-body__confirm">
            <label for="delete-confirm-input" class="delete-body__confirm-label">
              Type <span class="delete-body__confirm-token">DELETE</span> to confirm
            </label>
            <input
              id="delete-confirm-input"
              v-model="typedConfirm"
              type="text"
              class="delete-body__confirm-input"
              :class="{ 'is-valid': typedConfirm === 'DELETE' }"
              placeholder="DELETE"
              autocomplete="off"
              spellcheck="false"
            />
          </div>
        </template>

        <template v-else-if="entity === 'account'">
          <p class="delete-body__copy">
            You'll be removed from this organization. Your activity history will remain visible to
            your team.
          </p>
        </template>
        <template v-else>
          <p class="delete-body__copy">
            Are you sure you want to permanently delete
            {{ plural ? "these" : "this" }}
            {{ entity ?? "record" }}? This action cannot be reversed.
          </p>
        </template>
      </div>
    </template>

    <template #footer>
      <div class="delete-footer">
        <Button variant="outline" size="sm" @click="closeModal()">Cancel</Button>
        <Button
          variant="danger"
          size="sm"
          :loading="isDeleting"
          :disabled="isDeleting || (isOwner && typedConfirm !== 'DELETE')"
          @click="deleteRecord()"
        >
          <Icon icon="mdi:trash-can-outline" width="15" height="15" style="margin-right: 6px" />
          {{ isOwner ? "Permanently delete" : "Delete" }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref } from "vue";
import { Icon, Button, Modal } from "@/components/ui";

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
    default: false,
  },
  deleteFunction: {
    type: Function,
    required: true,
  },
  entity: {
    type: String,
    required: false,
    default: "Record",
  },
  plural: {
    type: Boolean,
    required: false,
    default: false,
  },
  isOwner: {
    type: Boolean,
    default: false,
  },
});

const typedConfirm = ref("");

const emit = defineEmits(["closeDeleteConfirmationModal", "refreshRecords"]);

const isDeleting = ref(false);

const closeModal = () => {
  emit("closeDeleteConfirmationModal");
};

const deleteRecord = async () => {
  if (props.deleteFunction && !isDeleting.value) {
    isDeleting.value = true;
    try {
      const res = await props.deleteFunction();
      if (res != null) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        closeModal();
        emit("refreshRecords");
      }
    } catch (error) {
      console.error("Error during deletion:", error);
    } finally {
      isDeleting.value = false;
    }
  }
};
</script>

<style scoped>
:deep(.ui-modal__dialog) {
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 24px 60px -12px rgba(15, 23, 42, 0.22),
    0 0 0 1px rgba(15, 23, 42, 0.04);
}

:deep(.ui-modal__header) {
  position: relative;
  padding: 22px 22px 10px;
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(239, 68, 68, 0.07) 0%, transparent 55%),
    linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
}

:deep(.ui-modal__body) {
  padding: 0;
}

:deep(.ui-modal__footer) {
  padding: 14px 22px 18px;
  background: #fafafa;
  border-top: 1px solid rgba(15, 23, 42, 0.05);
}

:deep(.ui-modal__close:hover) {
  background: rgba(15, 23, 42, 0.05);
  color: #111827;
}

.delete-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.delete-header__icon-wrap {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.delete-header__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
}

.delete-header__ring--outer {
  background: radial-gradient(circle, rgba(239, 68, 68, 0.14) 0%, transparent 70%);
  animation: delete-pulse 2.8s ease-in-out infinite;
}

.delete-header__ring--inner {
  inset: 4px;
  background: radial-gradient(circle, rgba(239, 68, 68, 0.22) 0%, transparent 72%);
}

.delete-header__icon {
  position: absolute;
  inset: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #fef2f2 0%, #fee2e2 100%);
  color: #dc2626;
  border-radius: 12px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 1px 2px rgba(220, 38, 38, 0.12),
    0 0 0 1px rgba(220, 38, 38, 0.12);
}

@keyframes delete-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.5;
  }
}

.delete-header__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 2px;
  min-width: 0;
  padding-right: 28px;
}

.delete-header__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #dc2626;
}

.delete-header__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.18);
}

.delete-header__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.012em;
  line-height: 1.35;
}

.delete-body {
  padding: 18px 22px 20px;
}

.delete-body__copy {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: #475569;
}

.delete-body__lede {
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 1.55;
  color: #475569;
}

.delete-body__list {
  list-style: none;
  margin: 0 0 16px;
  padding: 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  background: #fafafa;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 10px;
}

.delete-body__list li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #334155;
}

.delete-body__list-icon {
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  color: #ef4444;
  margin-top: 1px;
}

.delete-body__callout {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  padding: 10px 12px;
  background: linear-gradient(90deg, #fef2f2 0%, #fff5f5 100%);
  border: 1px solid rgba(220, 38, 38, 0.18);
  border-left: 3px solid #dc2626;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: #991b1b;
}

.delete-body__confirm {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.delete-body__confirm-label {
  font-size: 12px;
  font-weight: 500;
  color: #475569;
}

.delete-body__confirm-token {
  display: inline-block;
  padding: 1px 6px;
  margin: 0 1px;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid rgba(220, 38, 38, 0.2);
  border-radius: 4px;
}

.delete-body__confirm-input {
  width: 100%;
  height: 40px;
  padding: 0 14px;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-align: center;
  text-transform: uppercase;
  color: #0f172a;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  outline: none;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background 0.18s ease;
}

.delete-body__confirm-input::placeholder {
  color: #cbd5e1;
  font-weight: 500;
}

.delete-body__confirm-input:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}

.delete-body__confirm-input.is-valid {
  border-color: #10b981;
  background: #f0fdf4;
  color: #047857;
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12);
}

.delete-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
}
</style>

<style>
.ui-modal__header:has(.delete-header) .ui-modal__close {
  position: absolute;
  top: 10px;
  right: 10px;
  margin: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: #9ca3af;
  z-index: 2;
}

.ui-modal__header:has(.delete-header) .ui-modal__close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #111827;
}
</style>
